import {
  ArchiveIcon,
  ClockClockwiseIcon,
  DotsThreeVerticalIcon,
} from '@phosphor-icons/react';
import { ColumnDef } from '@tanstack/react-table';
import { format } from 'date-fns/format';
import { useState } from 'react';
import { toast } from 'sonner';
import colors from 'tailwindcss/colors';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import {
  ItemStatusEnum,
  useApiMutation,
  useTableDataQuery,
} from '@/modules/common';
import {
  ChangeStatusDialog,
  PaginatedTable,
  TableItemStatus,
} from '@/modules/common/components';

import { Mask } from '../types';

interface MasksTableProps {
  onClick: (item: Mask) => void;
}

export function MasksTable({ onClick }: MasksTableProps) {
  const { data, isFetching, refetch, ...tableProps } = useTableDataQuery<Mask>({
    url: '/v2/masks',
    persistState: true,
  });

  const [isUpdating, setIsUpdating] = useState(false);
  const [activeMask, setActiveMask] = useState<Mask | null>(null);
  const isArchived = activeMask?.status === ItemStatusEnum.Archived;

  const updateMaskMutation = useApiMutation<Mask>(
    '/masks',
    {
      method: 'PATCH',
    },
    {
      onError: () => {
        toast.error('Uh oh! Something went wrong.');
      },
      onSuccess: () => {
        refetch();
      },
    }
  );

  const toggleMaskStatus = async (style: Mask) => {
    await updateMaskMutation.mutateAsync({
      ...style,
      status:
        style.status === ItemStatusEnum.Archived
          ? ItemStatusEnum.Ready
          : ItemStatusEnum.Archived,
    });
  };

  const onArchiveRestoreClick = (
    event: React.SyntheticEvent<HTMLDivElement>,
    maskRow: Mask
  ) => {
    event.preventDefault();
    event.stopPropagation();
    setActiveMask(maskRow);
  };

  const toggleModal = (nextOpenState: boolean) => {
    if (!nextOpenState) {
      setActiveMask(null);
    }
  };

  const toggleStatusMask = async () => {
    if (activeMask?.id) {
      setIsUpdating(true);
      try {
        await toggleMaskStatus(activeMask as Mask);
        setActiveMask(null);
      } finally {
        setIsUpdating(false);
      }
    }
  };

  const columns: ColumnDef<Mask>[] = [
    {
      accessorKey: 'name',
      header: 'Mask Name',
      cell: ({ row }) => (
        <span className="line-clamp-1">{row.getValue('name')}</span>
      ),
    },
    {
      accessorKey: 'imageFileObject',
      header: 'Preview',
      cell: ({ row }) => {
        const imageUrl = row.original.imageFileObject.readUrl;

        return (
          <img
            src={imageUrl}
            alt="Mask preview image"
            className="w-auto h-auto max-w-[36px] max-h-[36px] object-cover border border-zinc-200"
          />
        );
      },
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => <TableItemStatus status={row.getValue('status')} />,
    },
    {
      accessorKey: 'updatedAt',
      header: 'Upload Date',
      cell: ({ row }) =>
        format(new Date(row.getValue('updatedAt')), 'yyyy-MM-dd'),
    },
    {
      size: 40,
      id: 'actions',
      enableHiding: false,
      cell: ({ row }) => {
        const isArchived = row.original.status === ItemStatusEnum.Archived;

        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="h-8 w-8 p-0"
                data-testid="masks-table-actions"
              >
                <span className="sr-only">Open menu</span>
                <DotsThreeVerticalIcon color={colors.zinc[400]} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                data-testid={isArchived ? 'restore' : 'archive'}
                className={cn(
                  !isArchived && 'focus:text-destructive text-destructive'
                )}
                onClick={(event) => onArchiveRestoreClick(event, row.original)}
              >
                {isArchived ? (
                  <ClockClockwiseIcon />
                ) : (
                  <ArchiveIcon color="hsl(var(--destructive))" />
                )}
                {isArchived ? 'Restore' : 'Archive'}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  return (
    <>
      <PaginatedTable<Mask>
        data={data}
        onClick={onClick}
        columns={columns}
        searchable
        searchPlaceholder="Search by mask name"
        emptyDescription="No masks uploaded yet"
        isLoading={isFetching}
        {...tableProps}
      />

      <ChangeStatusDialog
        onOpenChange={toggleModal}
        isArchived={isArchived}
        isUpdating={isUpdating}
        onSubmit={toggleStatusMask}
        isOpen={Boolean(activeMask?.id)}
        title={`${isArchived ? 'Restore' : 'Archive'} mask “${activeMask?.name}”?`}
        restoreDescription="Are you sure you want to restore this mask?"
        archiveDescription="Are you sure you want to archive this mask? You can restore it later if needed."
      />
    </>
  );
}
